<template>
  <!-- EXISTING LIST -->
  <SectionWrapper v-if="contracts?.length" class="mt-2">
    <h3>{{ t('pages.contracts.headlineExistingContracts') }}</h3>
    <!-- TODO: @Deprecated -->
    <DataTable
      v-if="contracts.length > 0"
      data-key="uiPluginUploadAllcontracts"
      :data="contracts"
      :columns="columnsV1"
      :isLoadingFinished="true"
      :reorderableRows="true"
      :tableActions="false"
      :showPrimaryToolbar="false"
      :showSecondaryToolbarButtons="false"
      :showSecondaryToolbar="false"
      :onRowClick="showContract"
      :onRowDblClick="gotoContract"
    />
    <ModalOrPage ref="showContractsRef">
      <template #default="{ state }">
        <ContractsDetail :uuid="state.uuid" :inModal="true" />
      </template>
    </ModalOrPage>
  </SectionWrapper>

  <h2 class="mt-12">
    {{ t('pages.contracts.headlineNewContract', { count: contracts.length }) }}
  </h2>

  <ContractsDetail
    :key="'new'"
    :isNew="true"
    :editMode="true"
    :inModal="true"
    :initialData="{
      pointOfSaleId: posID,
      customerGroupId: customerGroupID,
      customerId: customerID,
    }"
    :disabledFields="
      isRestrictedToPos
        ? {
            pointOfSaleId: true,
            customerGroupId: true,
            customerId: true,
          }
        : undefined
    "
    @submitSuccess="handleDetailSubmitSuccess"
  />
</template>

<script setup lang="ts">
import { ref } from 'vue';
import type { DataTableRowClickEvent, DataTableRowDoubleClickEvent } from 'primevue';
import { useI18n } from 'vue-i18n';

import type { ModalOrPageExpose } from '@/components/details/detail-modal-or-page/DetailModalOrPage.vue';
import { columnsV1 } from '@/views/contracts/configs/contractColumnConfig';
import ContractsDetail from '@/views/contracts/ContractsDetail.vue';
import type { ContractSimple } from '@/views/contracts/contractsTypes';
import type { PointOfSaleDetailSimple } from '@/views/pointofsales/pointOfSalesTypes';
import { ifClickOrDblClick } from '@/utils/clickOrDblClick';
import type {
  ContractV1Dto,
  CustomerGroupV1Dto,
  CustomerV1Dto,
  TicketPointOfSaleV1Dto,
} from '@/openapi';
import { eventBus } from '@/configs/eventBus';

const props = withDefaults(
  defineProps<{
    pos: TicketPointOfSaleV1Dto;
    contracts: ContractV1Dto[];
    isLoadingFinished?: boolean;
    isRestrictedToPos?: boolean;
  }>(),
  {
    isLoadingFinished: false,
    isRestrictedToPos: false,
  },
);

const emit = defineEmits<{
  (
    e: 'submitSuccess',
    payload: {
      id?: string;
      request?: ContractSimple;
      resp: ContractSimple;
    },
  ): void;
}>();

const { t } = useI18n();
const showContractsRef = ref<ModalOrPageExpose | null>(null);
const posID = ref<PointOfSaleDetailSimple['id'] | undefined>(props.pos?.id);
const customerID = ref<CustomerV1Dto['id'] | undefined>(props.pos?.customer?.id);
const customerGroupID = ref<CustomerGroupV1Dto['id'] | undefined>(
  props.pos?.customer?.customerGroup?.id,
);

function handleDetailSubmitSuccess(payload: {
  id?: string;
  request?: ContractSimple;
  resp: ContractSimple;
}) {
  // Important: bubble up to ActionDetail via plugin
  emit('submitSuccess', payload);
}

function showContract(event: DataTableRowClickEvent) {
  const contractID = event?.data?.id.meta('rawvalue');
  if (!contractID) return;
  ifClickOrDblClick(
    () => {
      /* single */
      showContractsRef.value?.openInModal({ uuid: contractID });
    },
    () => {
      /* double */
      eventBus.emit('goto', { path: `/contracts/${contractID}/edit` });
    },
  );
}

function gotoContract(event: DataTableRowDoubleClickEvent) {
  const contractID = event?.data?.id.meta('rawvalue');
  if (!contractID) return;
  eventBus.emit('goto', { path: `/contracts/${contractID}/edit` });
}
</script>
